/**
 * Session Warning Modal
 * Shows warning when session is about to expire with option to extend
 */

import {
  ClockCircleOutlined,
  LogoutOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { Button, Modal, Progress, Space, Typography } from 'antd';
import React, { useMemo } from 'react';

const { Text, Title } = Typography;

export interface SessionWarningModalProps {
  open: boolean;
  timeRemaining: number; // seconds
  onExtend: () => void;
  onLogout: () => void;
}

export const SessionWarningModal: React.FC<SessionWarningModalProps> = ({
  open,
  timeRemaining,
  onExtend,
  onLogout,
}) => {
  /**
   * Format time as MM:SS
   */
  const formattedTime = useMemo(() => {
    const minutes = Math.floor(timeRemaining / 60);
    const seconds = timeRemaining % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, [timeRemaining]);

  /**
   * Calculate progress percentage (2 minutes = 120 seconds = 100%)
   */
  const progress = useMemo(() => {
    const maxTime = 120; // 2 minutes
    const percentage = (timeRemaining / maxTime) * 100;
    return Math.max(0, Math.min(100, percentage));
  }, [timeRemaining]);

  /**
   * Get progress color based on time remaining
   */
  const progressColor = useMemo(() => {
    if (timeRemaining > 60) return '#52c41a'; // green
    if (timeRemaining > 30) return '#faad14'; // orange
    return '#f5222d'; // red
  }, [timeRemaining]);

  return (
    <Modal
      open={open}
      title={null}
      footer={null}
      closable={false}
      centered
      width={480}
      maskClosable={false}
      keyboard={false}
      styles={{
        body: {
          padding: '32px',
        },
      }}
    >
      <Space orientation="vertical" size="large" style={{ width: '100%' }}>
        {/* Header */}
        <div style={{ textAlign: 'center' }}>
          <ClockCircleOutlined
            style={{
              fontSize: 64,
              color: progressColor,
              marginBottom: 16,
            }}
          />
          <Title level={3} style={{ marginBottom: 8 }}>
            Session Expiring Soon
          </Title>
          <Text type="secondary">
            Your session will expire due to inactivity
          </Text>
        </div>

        {/* Timer Display */}
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              fontSize: 48,
              fontWeight: 'bold',
              color: progressColor,
              fontFamily: 'monospace',
              marginBottom: 16,
            }}
          >
            {formattedTime}
          </div>
          <Progress
            percent={progress}
            strokeColor={progressColor}
            showInfo={false}
            strokeWidth={8}
          />
        </div>

        {/* Message */}
        <div
          style={{
            padding: 16,
            backgroundColor: '#f5f5f5',
            borderRadius: 8,
            textAlign: 'center',
          }}
        >
          <Text>
            Would you like to stay signed in? Click <strong>Continue</strong> to
            extend your session, or <strong>Logout</strong> to end your session
            now.
          </Text>
        </div>

        {/* Action Buttons */}
        <Space
          style={{ width: '100%', justifyContent: 'center' }}
          size="middle"
        >
          <Button
            size="large"
            icon={<LogoutOutlined />}
            onClick={onLogout}
            style={{ minWidth: 120 }}
          >
            Logout
          </Button>
          <Button
            type="primary"
            size="large"
            icon={<ReloadOutlined />}
            onClick={onExtend}
            style={{ minWidth: 120 }}
          >
            Continue
          </Button>
        </Space>
      </Space>
    </Modal>
  );
};

export default SessionWarningModal;
